// src/components/customers/CustomerTable.tsx
import React from "react";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import Select from "@/components/shared/ui/Select";

import {
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
} from "react-icons/fi";

/* ===========================================================================
 * Types
 * =========================================================================== */

export type CustomerStatus = "ACTIVE" | "INACTIVE" | "BLOCKED";
export type CustomerStatusFilter = "" | CustomerStatus;

export type CustomerTableFilters = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  status?: CustomerStatusFilter;
};

export type CustomerRow = {
  id: number | string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: CustomerStatus;
};

/* ===========================================================================
 * Component
 * =========================================================================== */

export default function CustomerTable({
  // rows,
  pageRows,
  serialStart,
  filters,
  setFilters,
  onEdit,
  onDelete,
  loading = false,
}: {
  rows: CustomerRow[];
  pageRows: CustomerRow[];
  serialStart: number;
  filters: CustomerTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<CustomerTableFilters>>;
  onEdit: (u: CustomerRow) => void;
  onDelete: (u: CustomerRow) => void;
  loading?: boolean;
}) {
  const showEmpty = !loading && pageRows.length === 0;
  const showLoading = loading && pageRows.length === 0;

  return (
    // Square bottom on purpose: the pagination bar below completes this card
    // and owns the bottom corners.
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
      {/* Desktop table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>

              <ThWithFilter
                label="First name"
                value={filters.firstName}
                onChange={(v) =>
                  setFilters((f) => ({
                    ...f,
                    firstName: v,
                  }))
                }
              />

              <ThWithFilter
                label="Last name"
                value={filters.lastName}
                onChange={(v) =>
                  setFilters((f) => ({
                    ...f,
                    lastName: v,
                  }))
                }
              />

              <ThWithFilter
                label="Email"
                value={filters.email}
                onChange={(v) =>
                  setFilters((f) => ({
                    ...f,
                    email: v,
                  }))
                }
              />

              <ThWithFilter
                label="Phone"
                value={filters.phone}
                onChange={(v) =>
                  setFilters((f) => ({
                    ...f,
                    phone: v,
                  }))
                }
              />

              <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide">
                <div className="flex items-center gap-1.5">
                  <span>Status</span>
                  <ColumnFilter label="Status">
                    <Select
                      value={filters.status ?? ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          status: e.target.value as CustomerStatusFilter,
                        }))
                      }
                      options={["ACTIVE", "INACTIVE", "BLOCKED"]}
                    />
                  </ColumnFilter>
                </div>
              </th>

              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {showLoading ? (
              <LoadingRow colSpan={7} message="Loading customers…" />
            ) : showEmpty ? (
              <LoadingRow colSpan={7} message="No customers found." />
            ) : (
              pageRows.map((u, i) => (
                <tr
                  key={u.id}
                  className="cursor-pointer hover:bg-gray-50/60"
                  onClick={() => onEdit(u)}
                >
                  <Td>{serialStart + i + 1}</Td>

                  <Td>
                    <CustomerAvatar first={u.firstName} last={u.lastName} />
                  </Td>

                  <Td>{u.lastName}</Td>

                  <Td className="max-w-[260px] truncate">{u.email}</Td>

                  <Td className="max-w-[180px] truncate">{u.phone}</Td>

                  <Td>
                    <StatusBadge status={u.status} />
                  </Td>

                  <Td className="text-right">
                    <ActionsCell
                      row={u}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <MobileList
        pageRows={pageRows}
        serialStart={serialStart}
        showEmpty={showEmpty}
        showLoading={showLoading}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
}

/* ===========================================================================
 * Sub Components
 * =========================================================================== */

function Th({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function ThWithFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
}) {
  return (
    <Th>
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        <ColumnFilter label={label}>
          <Input
            placeholder="Contains…"
            value={value || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(e.target.value)
            }
          />
        </ColumnFilter>
      </div>
    </Th>
  );
}

function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}

function CustomerAvatar({ first, last }: { first: string; last: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
        {(first?.[0] ?? "").toUpperCase()}
        {(last?.[0] ?? "").toUpperCase()}
      </div>
      <span className="text-gray-900">{first}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: CustomerStatus }) {
  if (status === "ACTIVE") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
        <FiCheckCircle /> Active
      </span>
    );
  }

  if (status === "INACTIVE") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
        <FiXCircle /> Inactive
      </span>
    );
  }

  // BLOCKED
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
      <FiAlertTriangle /> Blocked
    </span>
  );
}

function LoadingRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="px-5 py-10 text-center text-sm text-gray-500"
      >
        {message}
      </td>
    </tr>
  );
}

/* ===========================================================================
 * Actions Cell
 * =========================================================================== */

function ActionsCell({
  row,
  onEdit,
  onDelete,
}: {
  row: CustomerRow;
  onEdit: (u: CustomerRow) => void;
  onDelete: (u: CustomerRow) => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      <ActionButton
        icon={<FiEdit2 />}
        tooltip="Edit"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(row);
        }}
        color="text-gray-600 hover:bg-gray-100"
      />

      <ActionButton
        icon={<FiTrash2 />}
        tooltip="Delete"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(row);
        }}
        color="text-red-600 hover:bg-red-50"
      />
    </div>
  );
}

function ActionButton({
  icon,
  tooltip,
  onClick,
  color,
}: {
  icon: React.ReactNode;
  tooltip: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  color: string;
}) {
  return (
    <button
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${color}`}
      onClick={onClick}
      aria-label={tooltip}
      title={tooltip}
    >
      {icon}
    </button>
  );
}

/* ===========================================================================
 * Mobile List
 * =========================================================================== */

function MobileList({
  pageRows,
  serialStart,
  showEmpty,
  showLoading,
  onEdit,
  onDelete,
}: {
  pageRows: CustomerRow[];
  serialStart: number;
  showEmpty: boolean;
  showLoading: boolean;
  onEdit: (u: CustomerRow) => void;
  onDelete: (u: CustomerRow) => void;
}) {
  return (
    <div className="divide-y divide-gray-200 md:hidden">
      {showLoading ? (
        <div className="p-6 text-center text-sm text-gray-500">
          Loading customers…
        </div>
      ) : showEmpty ? (
        <div className="p-6 text-center text-sm text-gray-500">
          No customers found.
        </div>
      ) : (
        pageRows.map((u, i) => (
          <div key={u.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                  {(u.firstName?.[0] ?? "").toUpperCase()}
                  {(u.lastName?.[0] ?? "").toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {serialStart + i + 1}. {u.firstName} {u.lastName}
                  </div>
                  <div className="text-xs text-gray-500">{u.email}</div>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <ActionButton
                  icon={<FiEdit2 />}
                  tooltip="Edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(u);
                  }}
                  color="text-gray-600 hover:bg-gray-100"
                />
                <ActionButton
                  icon={<FiTrash2 />}
                  tooltip="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(u);
                  }}
                  color="text-red-600 hover:bg-red-50"
                />
              </div>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
              <div>
                <span className="text-gray-500">Phone:</span> {u.phone}
              </div>
              <div className="col-span-2">
                <StatusBadge status={u.status} />
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
